/**
 * @file TechnicalIndicatorChart.tsx
 * @description 技术指标 K 线图可视化组件，用于展示技术指标表格对应的 K 线图表
 *
 * 核心功能：
 * 1. 解析技术指标表格数据（JSON 格式）
 * 2. 提取 OHLC 数据并转换为 K 线图
 * 3. 支持多种技术指标的可视化（EMA, RSI, BOLL, MACD 等）
 * 4. 提供与 bot 页面 prompt 面板一致的图表体验
 *
 * @author Page
 * @date 2026-04-02
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Button, Spin} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import LightweightCandlestickChart, {KLineBar} from '../charts/LightweightCandlestickChart';

// K 线数据结构定义
interface CandlestickData {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    confirm: number; // 0=未完结，1=已完结
}

/**
 * 技术指标图表数据结构
 */
export interface TechnicalIndicatorChartData {
    instId: string;           // 合约 ID，如 BTC-USDT-SWAP
    timeframe: string;         // 时间周期，如 1d, 4h
    type: 'TechnicalIndicator'; // 类型标识
    tableData?: string;        // 原始表格数据（JSON 字符串）
    overall_trend?: string;    // 整体趋势描述
}

interface TechnicalIndicatorChartProps {
    data: TechnicalIndicatorChartData;
    height?: number;
    onBack?: () => void;
}

/**
 * 将 CandlestickData[] 转换为 KLineBar[] 格式
 */
const candlestickDataToKLineBars = (candles: CandlestickData[]): KLineBar[] => {
    return candles
        .filter(c => c && Number.isFinite(c.timestamp))
        .map(c => ({
            time: c.timestamp,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume || 0,
            confirmed: c.confirm === 1
        }))
        .sort((a, b) => a.time - b.time);
};

/**
 * 从表格数据中提取 instId 和 timeframe
 */
const extractMetadataFromTable = (tableData: string): { instId?: string; timeframe?: string } => {
    try {
        // 尝试从标题行提取信息，如 "### ETH-USDT-SWAP-1d 技术指标表格"
        const titleMatch = tableData.match(/#+\s*([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)-(\d+[a-zA-Z]+)\s*/i);
        if (titleMatch) {
            return {
                instId: titleMatch[1],
                timeframe: titleMatch[2]
            };
        }

        // 尝试从表格第一行提取，如 "| 1d | 04/01 00:00 | ..."
        const firstLineMatch = tableData.match(/\|\s*(\d+[a-zA-Z]+)\s*\|/i);
        if (firstLineMatch) {
            return {timeframe: firstLineMatch[1]};
        }

        return {};
    } catch (error) {
        // console.error('提取元数据失败:', error);
        return {};
    }
};

/**
 * 解析时间字符串为时间戳
 */
const parseTimeToTimestamp = (timeStr: string): number | null => {
    if (!timeStr || timeStr.trim() === '') return null;

    // 匹配 "MM/DD HH:mm" 格式
    const match = timeStr.match(/^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})$/);
    if (match) {
        const month = parseInt(match[1]) - 1;
        const day = parseInt(match[2]);
        const hours = parseInt(match[3]);
        const minutes = parseInt(match[4]);

        const date = new Date();
        date.setFullYear(date.getFullYear(), month, day);
        date.setHours(hours, minutes, 0, 0);
        return date.getTime();
    }

    // 匹配 "HH:mm" 格式
    const timeOnlyMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (timeOnlyMatch) {
        const hours = parseInt(timeOnlyMatch[1]);
        const minutes = parseInt(timeOnlyMatch[2]);

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date.getTime();
    }

    return null;
};

/**
 * 解析 OHLC 数值
 */
const parseOHLCValue = (value: string): number => {
    if (!value || value.trim() === '') return 0;

    const cleanValue = value.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleanValue);

    if (isNaN(num) || !isFinite(num)) return 0;
    if (num <= 0) return 0;

    return Number(num.toFixed(2));
};

/**
 * 检测时间顺序
 */
const detectTimeOrder = (timeColumns: string[]): 'normal' | 'reversed' => {
    if (timeColumns.length < 2) return 'normal';

    // 如果第一个时间包含 "00:00" 且最后一个不包含，说明是倒序
    const isFirstMidnight = timeColumns[0].includes('00:00');
    const isLastMidnight = timeColumns[timeColumns.length - 1].includes('00:00');

    if (isFirstMidnight && !isLastMidnight) return 'reversed';
    return 'normal';
};

/**
 * 解析 JSON 格式的表格数据中的 K 线数据和技术指标
 */
const parseIndicatorData = (data: string) => {
    let currentTimeFrame = '5m';

    try {
        // console.log('[数据解析] 输入数据预览:', data.substring(0, 300));

        let tableData: { headers: string[]; rows: string[][] };

        // 尝试解析 JSON
        try {
            const parsed = JSON.parse(data);
            // console.log('[数据解析] JSON 解析成功，parsed:', parsed);

            // 检查是否有 headers 和 rows 属性（使用 hasOwnProperty）
            if (parsed && typeof parsed === 'object' &&
                Array.isArray(parsed.headers) &&
                Array.isArray(parsed.rows)) {
                tableData = parsed;
                // console.log('[数据解析] 表格结构验证通过 - headers:', parsed.headers.length, 'rows:', parsed.rows.length);
            } else {
                // console.error('[数据解析] JSON 结构不正确 - 类型检查失败:', typeof parsed, Array.isArray(parsed), parsed?.headers, parsed?.rows);
                throw new Error('JSON 结构不正确：缺少 headers 或 rows');
            }
        } catch (e: any) {
            // console.warn('[数据解析] JSON 解析失败:', e.message);
            // console.warn('[数据解析] 尝试作为 Markdown 表格处理');

            // 尝试作为 Markdown 表格处理
            return parseMarkdownTable(data);
        }

        const result: CandlestickData[] = [];
        const indicatorData: any = {};
        const timeFormatterMap: Record<number, string> = {};

        // 基础验证
        if (!tableData.headers || !tableData.rows || tableData.rows.length === 0) {
            // console.warn('[数据解析] 数据结构不完整');
            return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
        }

        // 提取元信息（timeframe）
        if (tableData.headers && tableData.headers.length > 0) {
            const firstHeader = tableData.headers[0];
            if (firstHeader && firstHeader.trim() !== '') {
                currentTimeFrame = firstHeader.trim();
            }
        }

        // 数据处理
        const timeColumns = tableData.headers.slice(1);
        const timeOrder = detectTimeOrder(timeColumns);
        const isTimeReversed = timeOrder !== 'normal';

        // console.log('[数据解析] 时间列数量:', timeColumns.length, '时间顺序:', timeOrder);

        // 定位核心数据行
        const openRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('open'));
        const highRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('high'));
        const lowRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('low'));
        const closeRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('close'));
        const volumeRow = tableData.rows.find(row => row[0] && (row[0].toLowerCase().includes('volume') || row[0].toLowerCase() === 'vol'));

        if (!openRow || !highRow || !lowRow || !closeRow) {
            // console.warn('[数据解析] 缺少必要的 OHLC 数据行');
            return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
        }

        // console.log('[数据解析] 找到 OHLC 数据行');

        // 解析每一列数据
        for (let i = 0; i < timeColumns.length; i++) {
            const timeStr = timeColumns[i];
            let timestamp = parseTimeToTimestamp(timeStr);

            // 如果时间解析失败，生成伪时间戳
            if (timestamp === null) {
                const interval = 5 * 60 * 1000; // 默认 5 分钟间隔
                timestamp = Date.now() - (isTimeReversed ? i : (timeColumns.length - 1 - i)) * interval;
            }

            timeFormatterMap[Math.floor(timestamp / 1000)] = timeStr;

            const open = parseOHLCValue(openRow[i + 1] || '0');
            const high = parseOHLCValue(highRow[i + 1] || '0');
            const low = parseOHLCValue(lowRow[i + 1] || '0');
            const close = parseOHLCValue(closeRow[i + 1] || '0');
            const volume = volumeRow ? parseOHLCValue(volumeRow[i + 1] || '0') : 0;

            if (open > 0 && high >= low && high >= open && high >= close) {
                result.push({
                    timestamp,
                    open,
                    high,
                    low,
                    close,
                    volume,
                    confirm: (isTimeReversed ? i === 0 : i === timeColumns.length - 1) ? 0 : 1
                });
            }
        }

        // 按时间升序排序
        result.sort((a, b) => a.timestamp - b.timestamp);
        // console.log(`[数据解析] K 线数据已按时间正序排序 (${result.length}条)`);

        // 技术指标行识别
        const isValidIndicatorRow = (row: string[]): boolean => {
            const name = row[0]?.toLowerCase().trim();
            if (!name || name.length < 2) return false;

            // 排除 OHLC 或 Volume 行
            const ohlcKeywords = ['open', 'high', 'low', 'close', 'volume'];
            if (ohlcKeywords.some(keyword => name.includes(keyword))) return false;

            // 检查是否为有效的技术指标格式
            const indicatorPatterns = [
                /^ema\(\d+\)(?:-value)?$/i,
                /^sma\(\d+\)(?:-value)?$/i,
                /^wma\(\d+\)(?:-value)?$/i,
                /^rsi\(\d+\)(?:-value)?$/i,
                /^boll\(\d+\)(?:-(ub|mb|lb))?$/i,
                /^macd\(\d+(?:\s*,\s*\d+){0,2}\)(?:-(dif|diff|dea|bar|macd|hist|value))?$/i,
                /^kdj\(\d+\)(?:-value)?$/i,
                /^cci\(\d+\)(?:-value)?$/i,
                /^atr\(\d+\)(?:-value)?$/i,
                /^obv\(\d+\)(?:-value)?$/i,
                /^adx\(\d+\)(?:-value)?$/i
            ];

            return indicatorPatterns.some(pattern => pattern.test(name));
        };

        const indicatorRows = tableData.rows.filter(isValidIndicatorRow);
        // console.log('[数据解析] 找到的技术指标行数量:', indicatorRows.length);

        // 处理技术指标数据
        for (const indicatorRow of indicatorRows) {
            const indicatorName = indicatorRow[0];
            if (!indicatorName) continue;

            const normalizedName = indicatorName.toLowerCase().trim();

            // 解析指标名称和参数
            const match = normalizedName.match(/^([a-z]+)\(([^)]+)\)(?:-(.+))?$/i);
            if (!match) continue;

            const [, type, params, suffix] = match;
            const period = parseInt(params.split(',')[0] || '0') || 20;
            const seriesKey = `${type.toUpperCase()}_${type.toLowerCase()}_${period}`;

            // console.log(`[数据解析] 处理技术指标：${indicatorName} -> ${seriesKey}`);

            // 映射指标值到时间戳
            const indicatorValues: Array<{
                time: number;
                value?: number;
                upper?: number;
                middle?: number;
                lower?: number
            }> = [];

            result.forEach((candlestick, index) => {
                const tableIndex = isTimeReversed ? (result.length - 1 - index) : index;
                const valueStr = indicatorRow[tableIndex + 1];
                const value = parseOHLCValue(valueStr || '0');

                if (isNaN(value) || !isFinite(value)) return;

                // BOLL/MACD等特殊指标：多行合并到统一数据结构，避免后行覆盖前行
                if (type.toUpperCase() === 'BOLL' || type.toUpperCase() === 'MACD') {
                    const existingData = indicatorData[seriesKey] as Array<any> | undefined;
                    let targetArray = existingData || indicatorValues;
                    let entry = targetArray.find((e: any) => e.time === candlestick.timestamp);
                    if (!entry) {
                        entry = {time: candlestick.timestamp};
                        targetArray.push(entry);
                    }
                    if (type.toUpperCase() === 'BOLL') {
                        if (suffix?.toUpperCase() === 'UB' || normalizedName.includes('upper') || normalizedName.includes('上轨')) {
                            entry.upper = value;
                        } else if (suffix?.toUpperCase() === 'MB' || normalizedName.includes('middle') || normalizedName.includes('中轨')) {
                            entry.middle = value;
                        } else if (suffix?.toUpperCase() === 'LB' || normalizedName.includes('lower') || normalizedName.includes('下轨')) {
                            entry.lower = value;
                        } else {
                            entry.middle = value;
                        }
                    } else {
                        // MACD：DIF→diff, DEA→signal, BAR→histogram
                        const suffixUpper = suffix?.toUpperCase() || '';
                        if (suffixUpper === 'DIF' || suffixUpper === 'DIFF' || normalizedName.includes('dif')) {
                            entry.diff = value;
                        } else if (suffixUpper === 'DEA') {
                            entry.signal = value;
                        } else if (suffixUpper === 'BAR' || suffixUpper === 'HIST' || suffixUpper === 'MACD' || suffixUpper === 'VALUE'
                            || normalizedName.includes('bar') || normalizedName.includes('hist')) {
                            entry.histogram = value;
                        } else {
                            entry.value = value;
                        }
                    }
                } else {
                    indicatorValues.push({
                        time: candlestick.timestamp,
                        value
                    });
                }
            });

            if (indicatorValues.length > 0) {
                indicatorData[seriesKey] = indicatorValues;
            }
        }

        // console.log('[数据解析] 完成，技术指标数量:', Object.keys(indicatorData).length);

        return {
            candlestickData: result,
            indicators: indicatorData,
            timeFrame: currentTimeFrame,
            timeFormatterMap
        };

    } catch (error) {
        // console.error('[数据解析] 失败:', error);
        return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
    }
};

/**
 * 解析 Markdown 格式的表格数据（备用方案）
 */
const parseMarkdownTable = (data: string) => {
    try {
        // console.log('[Markdown 表格解析] 开始解析');

        const lines = data.split('\n').filter(line => line.trim() !== '');
        if (lines.length < 3) {
            // console.warn('[Markdown 表格解析] 行数不足');
            return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
        }

        // 解析表头
        const headers = lines[0].split('|')
            .map(h => h.trim())
            .filter((h, i, arr) => {
                if (i === 0 && h === '') return false;
                if (i === arr.length - 1 && h === '') return false;
                return true;
            });

        // console.log('[Markdown 表格解析] 表头:', headers);

        // 解析数据行（跳过分隔行）
        const rows = lines.slice(2).map(line => {
            return line.split('|')
                .map(cell => cell.trim())
                .filter((cell, i, arr) => {
                    if (i === 0 && cell === '') return false;
                    if (i === arr.length - 1 && cell === '') return false;
                    return true;
                });
        });

        // console.log('[Markdown 表格解析] 数据行数量:', rows.length);

        // 使用 JSON 格式的解析逻辑继续处理
        return parseIndicatorDataFromStructure({headers, rows});

    } catch (error) {
        // console.error('[Markdown 表格解析] 失败:', error);
        return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
    }
};

/**
 * 从结构化的表格数据中解析 K 线和技术指标（通用逻辑）
 */
const parseIndicatorDataFromStructure = (tableData: { headers: string[]; rows: string[][] }) => {
    let currentTimeFrame = '5m';

    try {
        const result: CandlestickData[] = [];
        const indicatorData: any = {};
        const timeFormatterMap: Record<number, string> = {};

        // 基础验证
        if (!tableData.headers || !tableData.rows || tableData.rows.length === 0) {
            // console.warn('[结构化解析] 数据结构不完整');
            return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
        }

        // 提取元信息（timeframe）
        if (tableData.headers && tableData.headers.length > 0) {
            const firstHeader = tableData.headers[0];
            if (firstHeader && firstHeader.trim() !== '') {
                currentTimeFrame = firstHeader.trim();
            }
        }

        // 数据处理
        const timeColumns = tableData.headers.slice(1);
        const timeOrder = detectTimeOrder(timeColumns);
        const isTimeReversed = timeOrder !== 'normal';

        // console.log('[结构化解析] 时间列数量:', timeColumns.length, '时间顺序:', timeOrder);

        // 定位核心数据行
        const openRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('open'));
        const highRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('high'));
        const lowRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('low'));
        const closeRow = tableData.rows.find(row => row[0] && row[0].toLowerCase().includes('close'));
        const volumeRow = tableData.rows.find(row => row[0] && (row[0].toLowerCase().includes('volume') || row[0].toLowerCase() === 'vol'));

        if (!openRow || !highRow || !lowRow || !closeRow) {
            // console.warn('[结构化解析] 缺少必要的 OHLC 数据行');
            return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
        }

        // console.log('[结构化解析] 找到 OHLC 数据行');

        // 解析每一列数据
        for (let i = 0; i < timeColumns.length; i++) {
            const timeStr = timeColumns[i];
            let timestamp = parseTimeToTimestamp(timeStr);

            // 如果时间解析失败，生成伪时间戳
            if (timestamp === null) {
                const interval = 5 * 60 * 1000; // 默认 5 分钟间隔
                timestamp = Date.now() - (isTimeReversed ? i : (timeColumns.length - 1 - i)) * interval;
            }

            timeFormatterMap[Math.floor(timestamp / 1000)] = timeStr;

            const open = parseOHLCValue(openRow[i + 1] || '0');
            const high = parseOHLCValue(highRow[i + 1] || '0');
            const low = parseOHLCValue(lowRow[i + 1] || '0');
            const close = parseOHLCValue(closeRow[i + 1] || '0');
            const volume = volumeRow ? parseOHLCValue(volumeRow[i + 1] || '0') : 0;

            if (open > 0 && high >= low && high >= open && high >= close) {
                result.push({
                    timestamp,
                    open,
                    high,
                    low,
                    close,
                    volume,
                    confirm: (isTimeReversed ? i === 0 : i === timeColumns.length - 1) ? 0 : 1
                });
            }
        }

        // 按时间升序排序
        result.sort((a, b) => a.timestamp - b.timestamp);
        // console.log(`[结构化解析] K 线数据已按时间正序排序 (${result.length}条)`);

        // 技术指标行识别
        const isValidIndicatorRow = (row: string[]): boolean => {
            const name = row[0]?.toLowerCase().trim();
            if (!name || name.length < 2) return false;

            // 排除 OHLC 或 Volume 行
            const ohlcKeywords = ['open', 'high', 'low', 'close', 'volume'];
            if (ohlcKeywords.some(keyword => name.includes(keyword))) return false;

            // 检查是否为有效的技术指标格式
            const indicatorPatterns = [
                /^ema\(\d+\)(?:-value)?$/i,
                /^sma\(\d+\)(?:-value)?$/i,
                /^wma\(\d+\)(?:-value)?$/i,
                /^rsi\(\d+\)(?:-value)?$/i,
                /^boll\(\d+\)(?:-(ub|mb|lb))?$/i,
                /^macd\(\d+(?:\s*,\s*\d+){0,2}\)(?:-(dif|diff|dea|bar|macd|hist|value))?$/i,
                /^kdj\(\d+\)(?:-value)?$/i,
                /^cci\(\d+\)(?:-value)?$/i,
                /^atr\(\d+\)(?:-value)?$/i,
                /^obv\(\d+\)(?:-value)?$/i,
                /^adx\(\d+\)(?:-value)?$/i
            ];

            return indicatorPatterns.some(pattern => pattern.test(name));
        };

        const indicatorRows = tableData.rows.filter(isValidIndicatorRow);
        // console.log('[结构化解析] 找到的技术指标行数量:', indicatorRows.length);

        // 处理技术指标数据
        for (const indicatorRow of indicatorRows) {
            const indicatorName = indicatorRow[0];
            if (!indicatorName) continue;

            const normalizedName = indicatorName.toLowerCase().trim();

            // 解析指标名称和参数
            const match = normalizedName.match(/^([a-z]+)\(([^)]+)\)(?:-(.+))?$/i);
            if (!match) continue;

            const [, type, params, suffix] = match;
            const period = parseInt(params.split(',')[0] || '0') || 20;
            const seriesKey = `${type.toUpperCase()}_${type.toLowerCase()}_${period}`;

            // console.log(`[结构化解析] 处理技术指标：${indicatorName} -> ${seriesKey}`);

            // 映射指标值到时间戳
            const indicatorValues: Array<{
                time: number;
                value?: number;
                upper?: number;
                middle?: number;
                lower?: number
            }> = [];

            result.forEach((candlestick, index) => {
                const tableIndex = isTimeReversed ? (result.length - 1 - index) : index;
                const valueStr = indicatorRow[tableIndex + 1];
                const value = parseOHLCValue(valueStr || '0');

                if (isNaN(value) || !isFinite(value)) return;

                // BOLL/MACD等特殊指标：多行合并到统一数据结构，避免后行覆盖前行
                if (type.toUpperCase() === 'BOLL' || type.toUpperCase() === 'MACD') {
                    const existingData = indicatorData[seriesKey] as Array<any> | undefined;
                    let targetArray = existingData || indicatorValues;
                    let entry = targetArray.find((e: any) => e.time === candlestick.timestamp);
                    if (!entry) {
                        entry = {time: candlestick.timestamp};
                        targetArray.push(entry);
                    }
                    if (type.toUpperCase() === 'BOLL') {
                        if (suffix?.toUpperCase() === 'UB' || normalizedName.includes('upper') || normalizedName.includes('上轨')) {
                            entry.upper = value;
                        } else if (suffix?.toUpperCase() === 'MB' || normalizedName.includes('middle') || normalizedName.includes('中轨')) {
                            entry.middle = value;
                        } else if (suffix?.toUpperCase() === 'LB' || normalizedName.includes('lower') || normalizedName.includes('下轨')) {
                            entry.lower = value;
                        } else {
                            entry.middle = value;
                        }
                    } else {
                        // MACD：DIF→diff, DEA→signal, BAR→histogram
                        const suffixUpper = suffix?.toUpperCase() || '';
                        if (suffixUpper === 'DIF' || suffixUpper === 'DIFF' || normalizedName.includes('dif')) {
                            entry.diff = value;
                        } else if (suffixUpper === 'DEA') {
                            entry.signal = value;
                        } else if (suffixUpper === 'BAR' || suffixUpper === 'HIST' || suffixUpper === 'MACD' || suffixUpper === 'VALUE'
                            || normalizedName.includes('bar') || normalizedName.includes('hist')) {
                            entry.histogram = value;
                        } else {
                            entry.value = value;
                        }
                    }
                } else {
                    indicatorValues.push({
                        time: candlestick.timestamp,
                        value
                    });
                }
            });

            if (indicatorValues.length > 0) {
                indicatorData[seriesKey] = indicatorValues;
            }
        }

        // console.log('[结构化解析] 完成，技术指标数量:', Object.keys(indicatorData).length);

        return {
            candlestickData: result,
            indicators: indicatorData,
            timeFrame: currentTimeFrame,
            timeFormatterMap
        };

    } catch (error) {
        // console.error('[结构化解析] 失败:', error);
        return {candlestickData: [], indicators: {}, timeFrame: '5m', timeFormatterMap: {}};
    }
};

const TechnicalIndicatorChart: React.FC<TechnicalIndicatorChartProps> = ({
                                                                             data,
                                                                             height = 350,
                                                                             onBack
                                                                         }) => {
    const [loading, setLoading] = useState(true);
    const [klineData, setKlineData] = useState<KLineBar[]>([]);
    const [indicators, setIndicators] = useState<Record<string, any>>();
    const [timeFrame, setTimeFrame] = useState<string>('5m');
    const [timeFormatterMap, setTimeFormatterMap] = useState<Record<number, string>>({});
    const [error, setError] = useState<string | null>(null);

    // 计算所有解析出的技术指标的可见性（默认全部可见）
    const visibleIndicators = useMemo(() => {
        if (!indicators) return {};
        const vis: Record<string, boolean> = {};
        Object.keys(indicators).forEach(key => {
            const parts = key.split('_');
            if (parts.length >= 3) {
                const baseType = parts[0];
                const period = parts.slice(2).join('_');
                vis[`${baseType}_${period}`] = true;
            }
        });
        return vis;
    }, [indicators]);

    // 从表格数据中提取元信息
    const metadata = useMemo(() => {
        if (data.tableData) {
            const extracted = extractMetadataFromTable(data.tableData);
            return {
                instId: extracted.instId || data.instId,
                timeframe: extracted.timeframe || data.timeframe
            };
        }
        return {instId: data.instId, timeframe: data.timeframe};
    }, [data.instId, data.timeframe, data.tableData]);

    // 解析表格数据
    useEffect(() => {
        if (!data.tableData) {
            setError('缺少表格数据');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // console.log('[技术指标图表] 开始解析表格数据，长度:', data.tableData?.length);

            const parsed = parseIndicatorData(data.tableData!);

            // console.log('[技术指标图表] 解析结果:', {
            //     klineCount: parsed.candlestickData.length,
            //     indicatorsCount: Object.keys(parsed.indicators).length,
            //     timeframe: parsed.timeFrame
            // });

            if (parsed.candlestickData.length > 0) {
                const bars = candlestickDataToKLineBars(parsed.candlestickData);
                setKlineData(bars);
                setIndicators(parsed.indicators);
                setTimeFrame(parsed.timeFrame);
                setTimeFormatterMap(parsed.timeFormatterMap || {});
            } else {
                setError('未能从表格中解析出有效的 K 线数据');
            }
        } catch (err: any) {
            // console.error('[技术指标图表] 解析失败:', err);
            setError(err.message || '解析数据失败');
        } finally {
            setLoading(false);
        }
    }, [data.tableData]);

    if (loading) {
        return (
            <div style={{
                height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#0d1117',
                borderRadius: '6px'
            }}>
                <Spin/>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                height,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#0d1117',
                borderRadius: '6px',
                color: '#ff5252'
            }}>
                <div>{error}</div>
                <Button
                    onClick={() => {
                        if (onBack) onBack();
                    }}
                    type="link"
                    style={{marginTop: 8}}
                >
                    返回表格
                </Button>
            </div>
        );
    }

    return (
        <div style={{width: '100%'}}>
            {/* 顶部信息栏 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                borderRadius: '6px 6px 0 0',
                borderBottom: '1px solid #3a3a3a'
            }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                    <span style={{color: '#fff', fontWeight: 500}}>{metadata.instId}</span>
                    <span style={{color: '#999', fontSize: 12}}>{timeFrame}</span>
                    {data.overall_trend && (
                        <span style={{color: '#999', fontSize: 12}}>({data.overall_trend})</span>
                    )}
                </div>
            </div>

            {/* K 线图 */}
            <div style={{
                position: 'relative',
                backgroundColor: '#0d1117',
                borderRadius: '0 0 6px 6px',
                overflow: 'hidden'
            }}>
                {/* 返回按钮 */}
                {onBack && (
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowLeftOutlined/>}
                        onClick={onBack}
                        style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 10,
                            color: '#F0B429',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            borderRadius: '4px',
                            fontSize: 12
                        }}
                    >
                        表格数据
                    </Button>
                )}
                <LightweightCandlestickChart
                    data={klineData}
                    height={height}
                    timeFrame={timeFrame}
                    maxVisibleBars={klineData.length || 100}
                    lockRightEdge={false}
                    indicators={indicators}
                    visibleIndicators={visibleIndicators}
                    timeFormatterMap={timeFormatterMap}
                />
            </div>

            {/* 数据说明 */}
            <div style={{
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                marginTop: 4,
                borderRadius: '6px',
                fontSize: 12,
                color: '#999'
            }}>
                数据来源：表格 | K 线数量：{klineData.length} |
                时间范围：{klineData.length > 0 ?
                `${new Date(klineData[0].time).toLocaleDateString()} - ${new Date(klineData[klineData.length - 1].time).toLocaleDateString()}`
                : '未知'}
            </div>
        </div>
    );
};

export default TechnicalIndicatorChart;
